import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, CheckCircle, Clock, AlertCircle, TrendingUp, DollarSign, Loader2, ChevronDown, ChevronUp, MessageSquare } from "lucide-react";
import axios from "axios";
import config from "../config";
import { useAuth } from "../context/AuthContext";
import { useSuccessFailure } from "../context/SuccessFailureContext";

const EventPaymentsTab = ({ event, currentUser, onPaymentSuccess }) => {
  const { accessToken, user } = useAuth();
  const { showSuccessModal, showFailureModal } = useSuccessFailure();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingPayments, setIsLoadingPayments] = useState(true);
  const [payments, setPayments] = useState([]);
  const [paymentData, setPaymentData] = useState({
    reference_code: "",
    amount_paid: "",
    notes: ""
  });
  const [expandedNoteId, setExpandedNoteId] = useState(null);

  // Calculate analytics
  const paymentResults = payments.results || [];
  const totalPaid = paymentResults.reduce((sum, payment) => sum + parseFloat(payment.amount_paid || 0), 0);
  const ticketPrice = parseFloat(event.ticket_price || 0);
  const balance = Math.max(0, ticketPrice - totalPaid);
  const paymentProgress = ticketPrice > 0 ? Math.min(100, (totalPaid / ticketPrice) * 100) : 0;
  const hasPaid = ticketPrice > 0 && totalPaid >= ticketPrice;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: name === 'amount_paid' ? parseFloat(value) || 0 : value
    }));
  };

  // Fetch payment history for this event and user
  const fetchPayments = async () => {
    if (!event?.id || !user?.id) return;

    setIsLoadingPayments(true);
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/payments/event-payments/?event_id=${event.id}&user_id=${user.id}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );
      setPayments(response.data || { results: [] });
      // console.log('Fetched payments:', response.data);
    } catch (error) {
      // console.error('Error fetching payments:', error);
      showFailureModal("Error", "Failed to load payment history. Please try again later.");
    } finally {
      setIsLoadingPayments(false);
    }
  };

  // Fetch payments when component mounts
  useEffect(() => {
    fetchPayments();
  }, [event?.id, user?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!paymentData.reference_code || !paymentData.amount_paid) {
      showFailureModal("Missing Information", "Please fill in all required fields");
      return;
    }

    const amountPaid = parseFloat(paymentData.amount_paid);
    if (isNaN(amountPaid) || amountPaid <= 0) {
      showFailureModal("Invalid Amount", "Please enter a valid amount greater than zero");
      return;
    }

    setIsSubmitting(true);
    try {
      const paymentPayload = {
        event_id: event.id,
        payment_code: paymentData.reference_code,
        amount_paid: amountPaid,
        notes: paymentData.notes
      };

      await axios.post(
        `${config.API_BASE_URL}/payments/event-payments/`,
        paymentPayload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      // Refresh payments after successful submission
      await fetchPayments();

      showSuccessModal(
        "Payment Submitted Successfully",
        `Your payment of KES ${amountPaid.toLocaleString()} has been received and is being processed.`,
        () => {
          // Reset form
          setPaymentData({
            reference_code: "",
            amount_paid: "",
            notes: ""
          });
        }
      );
    } catch (error) {
      // console.error('Error submitting payment:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to submit payment. Please try again.';
      showFailureModal("Payment Failed", errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-center space-x-3">
          <CreditCard className="h-6 w-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-900">Event Payment</h2>
        </div>

        {/* Analytics Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full md:w-auto">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center space-x-2 text-blue-600">
              <DollarSign className="h-4 w-4" />
              <span className="text-sm font-medium">Ticket Price</span>
            </div>
            <p className="text-xl font-bold mt-1">KES {ticketPrice.toLocaleString()}</p>
          </div>

          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center space-x-2 text-green-600">
              <TrendingUp className="h-4 w-4" />
              <span className="text-sm font-medium">Paid</span>
            </div>
            <p className="text-xl font-bold mt-1">KES {totalPaid.toLocaleString()}</p>
          </div>

          <div className={`p-4 rounded-lg ${balance > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
            <div className={`flex items-center space-x-2 ${balance > 0 ? 'text-yellow-600' : 'text-green-600'}`}>
              {balance > 0 ? (
                <AlertCircle className="h-4 w-4" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
              <span className="text-sm font-medium">{balance > 0 ? 'Balance' : 'Fully Paid'}</span>
            </div>
            <p className="text-xl font-bold mt-1">
              {balance > 0 ? `KES ${balance.toLocaleString()}` : 'Complete'}
            </p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-gray-600">
          <span>Payment Progress</span>
          <span>{paymentProgress.toFixed(0)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full ${paymentProgress >= 95 ? 'bg-green-500' :
                paymentProgress >= 75 ? 'bg-yellow-500' :
                  paymentProgress >= 25 ? 'bg-orange-500' : 'bg-red-500'
              }`}
            style={{ width: `${paymentProgress}%` }}
          ></div>
        </div>
      </div>

      {/* Payment History */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Payment History</h3>

        {isLoadingPayments ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : paymentResults.length > 0 ? (
          <div className="overflow-hidden border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reference
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paymentResults.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(payment.created_at || payment.payment_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {payment.payment_code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      KES {parseFloat(payment.amount_paid).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                      {payment.notes ? (
                        <div className="flex items-start gap-2">
                          <MessageSquare className="h-4 w-4 mt-0.5 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className={`${expandedNoteId === payment.id ? '' : 'line-clamp-2'} text-sm text-gray-600`}>
                              {payment.notes}
                            </div>
                            {payment.notes.length > 100 && (
                              <button
                                onClick={() => setExpandedNoteId(expandedNoteId === payment.id ? null : payment.id)}
                                className="mt-1 text-xs font-medium text-blue-600 hover:text-blue-800 flex items-center"
                              >
                                {expandedNoteId === payment.id ? (
                                  <>
                                    <ChevronUp className="h-3 w-3 mr-1" /> Show less
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="h-3 w-3 mr-1" /> Show more
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      ) : null}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${payment.verification_status === 'verified' ? 'bg-green-100 text-green-800' :
                          payment.verification_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'}`}>
                        {payment.verification_status ?
                          (payment.verification_status.charAt(0).toUpperCase() + payment.verification_status.slice(1)) :
                          'Pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
            <p className="text-gray-500">No payment history found</p>
          </div>
        )}
      </div>

      {/* Payment Status */}
      {hasPaid ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 text-green-700">
            <CheckCircle className="h-5 w-5" />
            <span className="font-medium">Payment Complete</span>
          </div>
          <p className="mt-2 text-green-600">
            You've successfully paid KES {ticketPrice.toLocaleString()} for this event.
            You're all set for the event!
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-yellow-800">Payment Required</p>
                <p className="text-yellow-700 mt-1">
                  Please complete your payment to confirm your registration.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Payment Details</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Event</p>
                <p className="font-medium">{event.title}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Ticket Price</p>
                <p className="font-medium">KES {parseFloat(event.ticket_price).toLocaleString()}</p>
              </div>
            </div>

            {event.payment_instructions && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Payment Instructions</p>
                <div className="bg-gray-50 p-4 rounded-md whitespace-pre-line">
                  {event.payment_instructions}
                </div>
              </div>
            )}

            {event.payment_deadline && (
              <div className="flex items-center space-x-2 text-sm text-gray-600 mt-4">
                <Clock className="h-4 w-4" />
                <span>Payment deadline: {new Date(event.payment_deadline).toLocaleString()}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6 pt-4 border-t border-gray-200 space-y-4">
              <h4 className="text-md font-medium text-gray-900">Submit Payment Proof</h4>

              <div>
                <label htmlFor="reference_code" className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Reference Code *
                </label>
                <input
                  type="text"
                  id="reference_code"
                  name="reference_code"
                  value={paymentData.reference_code}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., M-PESA transaction code"
                  required
                />
              </div>

              <div>
                <label htmlFor="amount_paid" className="block text-sm font-medium text-gray-700 mb-1">
                  Amount Paid (KES) *
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">KES</span>
                  </div>
                  <input
                    type="number"
                    id="amount_paid"
                    name="amount_paid"
                    min="1"
                    step="1"
                    value={paymentData.amount_paid}
                    onChange={handleInputChange}
                    className="pl-12 w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter amount paid"
                    required
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Full ticket price: KES {event.ticket_price?.toLocaleString() || '0'}
                </p>
              </div>



              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes (Optional)
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  rows={3}
                  value={paymentData.notes}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Any additional information about your payment..."
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Payment Proof'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default EventPaymentsTab